import React, { useState, useCallback, useMemo } from 'react';
import SearchableSelect from './SearchableSelect';
import MultiSelectTags from './MultiSelectTags';
import ParticipationModalitySection from './sections/ParticipationModalitySection';
import ExpoZoneSection from './sections/ExpoZoneSection';
import { ParticipationModality, EventProgramParticipation } from '../types/profile.types';
import './CreateContactModal.css';

export interface CreateNegocioOrganizationOption {
  id: number;
  organizacion?: string | null;
}

export interface CreateNegocioPipelineOption {
  id: number;
  name: string;
  field_schema?: { key: string; label: string }[];
}

export interface CreateNegocioExistingDeal {
  organizationId: number;
  pipelineId: number;
  pipelineName: string;
  pipelineStage: string;
  priority: string;
  data?: Record<string, unknown>;
}

export interface CreateNegocioStageOption {
  id: string;
  label: string;
  color: string;
  position: number;
}

export interface CreateNegocioContactOption {
  id: number;
  nombre?: string | null;
  apellido?: string | null;
  correo?: string | null;
}

export interface CreateNegocioModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Lista de organizaciones para elegir */
  organizationsList?: CreateNegocioOrganizationOption[];
  /** Lista de pipelines (con field_schema para campos personalizados) */
  pipelinesList?: CreateNegocioPipelineOption[];
  /** Negocios existentes por organización y pipeline (para saber si ya existe y mostrar datos) */
  organizationNegociosList?: CreateNegocioExistingDeal[];
  /** Etapas por pipeline: pipelineId -> etapas (para el desplegable Etapa) */
  stagesByPipelineId?: Record<number, CreateNegocioStageOption[]>;
  /** Lista de contactos para "Asociar contactos" */
  contactsList?: CreateNegocioContactOption[];
  /** Opciones de modalidad para pipeline Scesde26 */
  participationModalityOptions?: { value: string; label: string }[];
  /** Al pulsar "Crear organización" se cierra este modal y se abre el de crear organización */
  onRequestCreateOrganization?: () => void;
  isDarkMode?: boolean;
  asSidebar?: boolean;
}

const CreateNegocioModal: React.FC<CreateNegocioModalProps> = ({
  isOpen,
  onClose,
  organizationsList = [],
  pipelinesList = [],
  organizationNegociosList = [],
  stagesByPipelineId = {},
  contactsList = [],
  participationModalityOptions = [],
  onRequestCreateOrganization,
  isDarkMode = false,
  asSidebar = false,
}) => {
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<number | null>(null);
  const [selectedPipelineId, setSelectedPipelineId] = useState<number | null>(null);
  const [selectedStageId, setSelectedStageId] = useState('');
  const [priority, setPriority] = useState<'high' | 'medium' | 'low'>('medium');
  const [selectedContactIds, setSelectedContactIds] = useState<number[]>([]);
  // Campos Scesde26
  const [participationModalities, setParticipationModalities] = useState<ParticipationModality[]>([]);
  const [agreedModality, setAgreedModality] = useState('');
  const [modalityObservations, setModalityObservations] = useState('');
  const [eventProgramParticipation, setEventProgramParticipation] = useState<string[]>([]);
  const [requestInformation, setRequestInformation] = useState(false);
  const [requestExpoInformation, setRequestExpoInformation] = useState(false);
  const [selectedStands, setSelectedStands] = useState<string[]>([]);
  const [confirmedStands, setConfirmedStands] = useState<string[]>([]);
  // Campos otros pipelines (field_schema)
  const [otherPipelineDealData, setOtherPipelineDealData] = useState<Record<string, string>>({});

  const stagesForPipeline = selectedPipelineId != null ? (stagesByPipelineId[selectedPipelineId] || []) : [];
  const stagesSorted = [...stagesForPipeline].sort((a, b) => a.position - b.position);

  const existingDeal = useMemo(() => {
    if (selectedOrganizationId == null || selectedPipelineId == null) return null;
    return organizationNegociosList.find(
      (n) => n.organizationId === selectedOrganizationId && n.pipelineId === selectedPipelineId
    ) ?? null;
  }, [organizationNegociosList, selectedOrganizationId, selectedPipelineId]);

  const organizationOptions = useMemo(() => {
    const used = new Set<string>();
    return organizationsList.map((org) => {
      const base = org.organizacion || `Organización ${org.id}`;
      const label = used.has(base) ? `${base} · #${org.id}` : base;
      used.add(base);
      return { id: org.id, label };
    });
  }, [organizationsList]);
  const organizationOptionLabels = useMemo(() => organizationOptions.map((o) => o.label), [organizationOptions]);
  const selectedOrganizationLabel = useMemo(
    () => (selectedOrganizationId != null ? organizationOptions.find((o) => o.id === selectedOrganizationId)?.label ?? '' : ''),
    [selectedOrganizationId, organizationOptions]
  );

  const pipelineOptions = useMemo(() => pipelinesList.map((p) => p.name), [pipelinesList]);
  const selectedPipelineName = useMemo(
    () => (selectedPipelineId != null ? pipelinesList.find((p) => p.id === selectedPipelineId)?.name ?? '' : ''),
    [selectedPipelineId, pipelinesList]
  );

  const stageOptions = useMemo(() => stagesSorted.map((s) => s.label), [stagesSorted]);
  const selectedStageLabel = useMemo(
    () => (selectedStageId ? stagesSorted.find((s) => s.id === selectedStageId)?.label ?? '' : ''),
    [selectedStageId, stagesSorted]
  );

  const priorityOptions = ['Alta', 'Media', 'Baja'];
  const priorityLabel = priority === 'high' ? 'Alta' : priority === 'medium' ? 'Media' : 'Baja';
  const PRIORITY_COLORS: Record<string, string> = { Alta: '#ef4444', Media: '#f59e0b', Baja: '#22c55e' };
  const getPriorityColor = (label: string) => PRIORITY_COLORS[label] ?? '#94a3b8';
  const selectedStageColor = selectedStageId ? stagesSorted.find((s) => s.id === selectedStageId)?.color : (stagesSorted[0]?.color ?? '#94a3b8');
  const getStageOptionColor = (label: string) => stagesSorted.find((s) => s.label === label)?.color;

  const contactOptions = useMemo(() => {
    const used = new Set<string>();
    return contactsList.map((c) => {
      const hasName = [c.nombre, c.apellido].filter(Boolean).join(' ').trim() !== '';
      const base = hasName
        ? (c.correo ? `${[c.nombre, c.apellido].filter(Boolean).join(' ')} (${c.correo})` : [c.nombre, c.apellido].filter(Boolean).join(' '))
        : (c.correo ? c.correo : `Sin nombre · #${c.id}`);
      const label = used.has(base) ? `${base} · #${c.id}` : base;
      used.add(base);
      return { id: c.id, label };
    });
  }, [contactsList]);

  const contactOptionLabels = useMemo(() => contactOptions.map((o) => o.label), [contactOptions]);

  const selectedContactLabels = useMemo(
    () => selectedContactIds.map((id) => contactOptions.find((o) => o.id === id)?.label).filter((l): l is string => !!l),
    [selectedContactIds, contactOptions]
  );

  const handleContactSelectChange = useCallback(
    (labels: string[]) => {
      setSelectedContactIds(labels.map((l) => contactOptions.find((o) => o.label === l)?.id).filter((id): id is number => id != null));
    },
    [contactOptions]
  );

  const resetForm = useCallback(() => {
    setSelectedOrganizationId(null);
    setSelectedPipelineId(null);
    setSelectedStageId('');
    setPriority('medium');
    setSelectedContactIds([]);
    setParticipationModalities([]);
    setAgreedModality('');
    setModalityObservations('');
    setEventProgramParticipation([]);
    setRequestInformation(false);
    setRequestExpoInformation(false);
    setSelectedStands([]);
    setConfirmedStands([]);
    setOtherPipelineDealData({});
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handlePipelineChange = (pipelineId: number | null) => {
    setSelectedPipelineId(pipelineId);
    if (pipelineId != null) {
      const stages = (stagesByPipelineId[pipelineId] || []).slice().sort((a, b) => a.position - b.position);
      setSelectedStageId(stages.length > 0 ? stages[0].id : '');
    } else {
      setSelectedStageId('');
    }
    setOtherPipelineDealData({});
  };

  const handleSubmit = () => {
    handleClose();
  };

  if (!isOpen) return null;

  const formContent = (
    <>
      <div className="create-contact-modal-header">
        <h2>Crear negocio</h2>
        <button type="button" className="create-contact-modal-close" onClick={handleClose} aria-label="Cerrar">
          ×
        </button>
      </div>
      <div className="create-contact-modal-body">
        <section className="create-contact-section">
          <h3>Organización</h3>
          <div className="create-contact-associate-deal">
            <div className="create-contact-deal-row">
              <label className="create-contact-label">Seleccionar organización</label>
              <div style={{ marginBottom: 12 }}>
                <SearchableSelect
                  value={selectedOrganizationLabel}
                  onChange={(value) => {
                    const org = organizationOptions.find((o) => o.label === value);
                    setSelectedOrganizationId(org ? org.id : null);
                    setSelectedPipelineId(null);
                    setSelectedStageId('');
                  }}
                  options={organizationOptionLabels}
                  placeholder="Seleccionar organización..."
                  allowCustomInput={false}
                  allowSearch={true}
                />
              </div>
            </div>
            {onRequestCreateOrganization && (
              <button
                type="button"
                className="create-contact-link-btn"
                style={{ marginTop: 4 }}
                onClick={onRequestCreateOrganization}
              >
                Crear organización
              </button>
            )}
          </div>
        </section>

        {selectedOrganizationId != null && (
          <section className="create-contact-section">
            <h3>Pipeline</h3>
            <div className="create-contact-associate-deal">
              <div className="create-contact-deal-row">
                <label className="create-contact-label">Seleccionar pipeline</label>
                <div style={{ marginBottom: 12 }}>
                  <SearchableSelect
                    value={selectedPipelineName}
                    onChange={(value) => {
                      const p = pipelinesList.find((x) => x.name === value);
                      handlePipelineChange(p ? p.id : null);
                    }}
                    options={pipelineOptions}
                    placeholder="Seleccionar pipeline..."
                    allowCustomInput={false}
                    allowSearch={false}
                  />
                </div>
              </div>
            </div>
          </section>
        )}

        {selectedPipelineId != null && (() => {
          if (existingDeal) {
            const isScesde26 = existingDeal.pipelineId === 1 || existingDeal.pipelineName === 'Scesde26';
            const d = existingDeal.data && typeof existingDeal.data === 'object' ? existingDeal.data : {};
            const priorityLabelExist = existingDeal.priority === 'high' ? 'Alta' : existingDeal.priority === 'low' ? 'Baja' : 'Media';
            const existingStageLabel = stagesSorted.find((s) => s.id === existingDeal.pipelineStage)?.label ?? existingDeal.pipelineStage;
            return (
              <section className="create-contact-section">
                <h3>Estado del negocio</h3>
                <p className="create-contact-hint" style={{ marginBottom: 12, fontWeight: 600 }}>
                  Ya existe un negocio de esta organización en el pipeline <strong>{existingDeal.pipelineName}</strong>.
                </p>
                <div className="create-contact-associate-deal">
                  <div className="create-contact-deal-row">
                    <label className="create-contact-label">Etapa</label>
                    <div className="create-contact-input" style={{ opacity: 0.9 }}>{existingStageLabel}</div>
                  </div>
                  <div className="create-contact-deal-row">
                    <label className="create-contact-label">Prioridad</label>
                    <div className="create-contact-input" style={{ opacity: 0.9 }}>{priorityLabelExist}</div>
                  </div>
                  {isScesde26 && d && (Array.isArray(d.participation_modalities) && d.participation_modalities.length > 0 || d.agreed_modality || Array.isArray(d.stands) && d.stands.length > 0) ? (
                    <>
                      {Array.isArray(d.participation_modalities) && (d.participation_modalities as string[]).length > 0 && (
                        <div className="create-contact-deal-row">
                          <label className="create-contact-label">Modalidad de participación</label>
                          <div className="create-contact-input" style={{ opacity: 0.9 }}>{(d.participation_modalities as string[]).join(', ')}</div>
                        </div>
                      )}
                      {d.agreed_modality && (
                        <div className="create-contact-deal-row">
                          <label className="create-contact-label">Modalidad acordada</label>
                          <div className="create-contact-input" style={{ opacity: 0.9 }}>{String(d.agreed_modality)}</div>
                        </div>
                      )}
                      {Array.isArray(d.stands) && (d.stands as string[]).length > 0 && (
                        <div className="create-contact-deal-row">
                          <label className="create-contact-label">Stands</label>
                          <div className="create-contact-input" style={{ opacity: 0.9 }}>{(d.stands as string[]).join(', ')}</div>
                        </div>
                      )}
                    </>
                  ) : null}
                  {!isScesde26 && (() => {
                    const selectedPipeline = pipelinesList.find((p) => p.id === existingDeal.pipelineId);
                    const schema = selectedPipeline?.field_schema ?? [];
                    if (schema.length === 0) return null;
                    return schema.map((field) => (
                      <div key={field.key} className="create-contact-deal-row">
                        <label className="create-contact-label">{(field.label || '').replace(/\s*\(actualizado\)\s*/gi, '').trim() || field.key}</label>
                        <div className="create-contact-input" style={{ opacity: 0.9 }}>
                          {d[field.key] != null ? String(d[field.key]) : '—'}
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              </section>
            );
          }

          const isScesde26 = selectedPipelineId === 1 || pipelinesList.find((p) => p.id === selectedPipelineId)?.name === 'Scesde26';
          const pipelineNameForLabel = selectedPipelineName || 'seleccionado';
          return (
            <>
              <section className="create-contact-section">
                <p className="create-contact-hint" style={{ marginBottom: 8 }}>
                  Pipeline seleccionado: <strong>{pipelineNameForLabel}</strong>. Complete los datos para crear el negocio.
                </p>
                <p className="create-contact-label" style={{ marginBottom: 12 }}>
                  La organización no tiene negocio en el pipeline {pipelineNameForLabel}. Complete los datos a continuación.
                </p>
              </section>
              <section className="create-contact-section">
                <div className="create-contact-associate-deal">
                  {stagesSorted.length > 0 && (
                    <div className="create-contact-deal-row">
                      <label className="create-contact-label">Etapa</label>
                      <div style={{ marginBottom: 12 }}>
                        <SearchableSelect
                          value={selectedStageLabel}
                          onChange={(value) => {
                            const s = stagesSorted.find((x) => x.label === value);
                            setSelectedStageId(s ? s.id : '');
                          }}
                          options={stageOptions}
                          placeholder="Seleccionar etapa..."
                          allowCustomInput={false}
                          allowSearch={false}
                          prefix={
                            <span
                              style={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                backgroundColor: selectedStageColor,
                                flexShrink: 0,
                              }}
                              aria-hidden
                            />
                          }
                          getOptionColor={getStageOptionColor}
                        />
                      </div>
                    </div>
                  )}
                  <div className="create-contact-deal-row">
                    <label className="create-contact-label">Prioridad</label>
                    <div style={{ marginBottom: 12 }}>
                      <SearchableSelect
                        value={priorityLabel}
                        onChange={(value) => {
                          setPriority(value === 'Alta' ? 'high' : value === 'Baja' ? 'low' : 'medium');
                        }}
                        options={priorityOptions}
                        placeholder="Seleccionar prioridad..."
                        allowCustomInput={false}
                        allowSearch={false}
                        prefix={
                          <span
                            style={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: getPriorityColor(priorityLabel),
                              flexShrink: 0,
                            }}
                            aria-hidden
                          />
                        }
                        getOptionColor={getPriorityColor}
                      />
                    </div>
                  </div>
                </div>
              </section>

              {isScesde26 && (
                <>
                  <section className="create-contact-section">
                    <h3>Modalidad de participación</h3>
                    <ParticipationModalitySection
                      selectedModalities={participationModalities}
                      onChange={setParticipationModalities}
                      agreedModality={agreedModality}
                      modalityObservations={modalityObservations}
                      eventProgramParticipation={eventProgramParticipation as EventProgramParticipation[]}
                      onEventProgramParticipationChange={setEventProgramParticipation}
                      requestInformation={requestInformation}
                      onRequestInformationChange={setRequestInformation}
                      createContactMode
                    />
                  </section>
                  <section className="create-contact-section create-contact-stands-wrap">
                    <h3>Stands de interés</h3>
                    <ExpoZoneSection
                      selectedStands={selectedStands}
                      confirmedStands={confirmedStands}
                      onChange={setSelectedStands}
                      onConfirmedChange={setConfirmedStands}
                      requestInformation={requestExpoInformation}
                      onRequestInformationChange={setRequestExpoInformation}
                      createContactMode
                    />
                  </section>
                </>
              )}

              {!isScesde26 && (() => {
                const selectedPipeline = pipelinesList.find((p) => p.id === selectedPipelineId);
                const otherFieldSchema = selectedPipeline?.field_schema ?? [];
                if (otherFieldSchema.length === 0) {
                  return (
                    <section className="create-contact-section">
                      <p className="create-contact-hint">
                        El negocio se creará en el pipeline <strong>{selectedPipeline?.name ?? 'este pipeline'}</strong>. Este pipeline no tiene campos personalizados definidos.
                      </p>
                    </section>
                  );
                }
                return (
                  <section className="create-contact-section">
                    <p className="create-contact-hint">Complete los campos del pipeline seleccionado.</p>
                    <div className="create-contact-associate-deal">
                      {otherFieldSchema.map((field) => (
                        <div key={field.key} className="create-contact-deal-row">
                          <label className="create-contact-label" htmlFor={`create-negocio-deal-${field.key}`}>
                            {(field.label || '').replace(/\s*\(actualizado\)\s*/gi, '').trim() || field.key}
                          </label>
                          <input
                            id={`create-negocio-deal-${field.key}`}
                            type="text"
                            className="create-contact-input"
                            value={otherPipelineDealData[field.key] ?? ''}
                            onChange={(e) => setOtherPipelineDealData((prev) => ({ ...prev, [field.key]: e.target.value }))}
                            placeholder={(field.label || '').replace(/\s*\(actualizado\)\s*/gi, '').trim() || field.key}
                          />
                        </div>
                      ))}
                    </div>
                  </section>
                );
              })()}

              <section className="create-contact-section">
                <h3>Asociar contactos</h3>
                <div className="create-contact-associate-org">
                  <label className="create-contact-label">Contactos asignados a este negocio</label>
                  {contactsList.length === 0 ? (
                    <p className="create-contact-hint" style={{ marginBottom: 12 }}>No hay contactos cargados.</p>
                  ) : (
                    <MultiSelectTags
                      options={contactOptionLabels}
                      selectedValues={selectedContactLabels}
                      onChange={handleContactSelectChange}
                      placeholder="Buscar o seleccionar contactos..."
                    />
                  )}
                </div>
              </section>
            </>
          );
        })()}
      </div>
      <div className="create-contact-modal-footer">
        <button type="button" className="create-contact-footer-btn secondary" onClick={handleClose}>
          Cancelar
        </button>
        {existingDeal ? (
          <button type="button" className="create-contact-footer-btn primary" onClick={handleClose}>
            Cerrar
          </button>
        ) : (
          <button
            type="button"
            className="create-contact-footer-btn primary"
            onClick={handleSubmit}
          >
            Crear negocio
          </button>
        )}
      </div>
    </>
  );

  if (asSidebar) {
    return (
      <div className={`create-contact-sidebar-inner ${isDarkMode ? 'dark' : ''}`}>
        {formContent}
      </div>
    );
  }

  return (
    <div className="create-contact-modal-overlay" onClick={handleClose}>
      <div
        className={`create-contact-modal ${isDarkMode ? 'dark' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {formContent}
      </div>
    </div>
  );
};

export default CreateNegocioModal;
